import authApi from "api/landlord/auth";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant, Input } from "shared/components";
import logoSquare from "shared/branding/logo-square.svg";
import { getErrorMessage } from "utils/error";
import { useFormTyped } from "shared/hooks";
import { object, string } from "yup";
import { useEffect } from "react";

enum SSOErrorMessages {
  SSO_IS_NOT_ENABLED = "sso_not_enabled",
  ORGANIZATION_NOT_FOUND = "organization_not_found"
}

export const LoginWithSSO = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();

  const { values, register, isSubmitting, onSubmit, setErrors } = useFormTyped(
    {
      email: state?.email || ""
    },
    object({
      email: string().email().required("Email is required")
    })
  );

  useEffect(() => {
    const errorParam = searchParams.get("error");

    if (errorParam) setFormattedErrorMessage(errorParam);
  }, [searchParams]);

  const handleLoginWithSSO = async () => {
    try {
      const response = await authApi.sso(values.email, state?.from);

      const redirectUrl = response.auth_url;

      window.location.href = redirectUrl;
    } catch (e) {
      const errorMessage = getErrorMessage(e);

      setFormattedErrorMessage(errorMessage);
    }
  };

  const setFormattedErrorMessage = (errorMessage: string) => {
    const formattedMessage = [
      SSOErrorMessages.SSO_IS_NOT_ENABLED,
      SSOErrorMessages.ORGANIZATION_NOT_FOUND
    ].includes(errorMessage as SSOErrorMessages)
      ? "SSO is not configured for this email address. Contact your administrator."
      : errorMessage;

    setErrors({
      email:
        formattedMessage ||
        "An error occurred. Please try again or contact your administrator."
    });
  };

  return (
    <div className="animate-appear flex min-h-screen items-center justify-center bg-background-900 p-2">
      <div className="h-full w-full max-w-[400px] rounded-lg border border-alpha-100 bg-background-1000 p-8 shadow-xl">
        <div className="flex items-center gap-4">
          <img alt="Boom logo" className="h-10 w-10" src={logoSquare} />
          <div>
            <p className="text-base font-bold">Boom</p>
            <p className="text-sm font-medium text-content-200">
              for Property Managers
            </p>
          </div>
        </div>
        <div className="my-6 border-b border-alpha-40" />
        <p className="mb-2 text-xl font-bold text-content-1000">
          Sign in with SSO
        </p>
        <p className="mb-6 text-sm font-medium text-content-400">
          Enter your email below to continue with SSO
        </p>
        <form
          onSubmit={onSubmit({
            onSuccess: handleLoginWithSSO
          })}
        >
          <Input
            id="email"
            label="Email"
            placeholder="example@gmail.com"
            required={true}
            {...register("email")}
          />

          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            className="mt-6 w-full"
            type="submit"
            loading={isSubmitting}
          >
            Continue
          </Button>

          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.BASE}
            className="mt-2 w-full"
            onClick={() => navigate("/login")}
          >
            Cancel
          </Button>
        </form>
      </div>
    </div>
  );
};
